import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { FaComments, FaExpandArrowsAlt, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
// import artistpublicprofileBackground from '../assets/Artistpublicprofile.png'; // File not found, using fallback
import { API_ENDPOINTS } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';
import { saveAppreciationActivity, checkExistingActivity, ACTIVITY_TYPES } from '../utils/activityService';
import ArtAnalysisHotspots from '../components/ArtAnalysisHotspots';
import ReimagineStudio from './ReimagineStudio';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from '../components/UpgradeQuotaModal';

/** Hotspot badges read 1→4 top-to-bottom. Backend often sends narrative order while (x,y) are geographic. */
function sortAppreciationPointsByVisualTopToBottom(points) {
  if (!Array.isArray(points) || points.length < 2) return points;
  const allHaveY = points.every(
    (p) => typeof p?.point?.y === 'number' && Number.isFinite(p.point.y)
  );
  if (!allHaveY) return points;
  return [...points]
    .map((p, originalIndex) => ({ p, originalIndex }))
    .sort((a, b) => {
      const dy = a.p.point.y - b.p.point.y;
      if (dy !== 0) return dy;
      const ax = typeof a.p.point?.x === 'number' && Number.isFinite(a.p.point.x) ? a.p.point.x : 0;
      const bx = typeof b.p.point?.x === 'number' && Number.isFinite(b.p.point.x) ? b.p.point.x : 0;
      if (ax !== bx) return ax - bx;
      return a.originalIndex - b.originalIndex;
    })
    .map(({ p }) => p);
}

export default function ArtAppreciationFeedback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkQuotaBeforeUse, recordUsage, showUpgradeModal, upgradeModalFeature, dismissUpgradeModal } = useFeatureQuota();
  const { 
    imageUrl, 
    title, 
    technique, 
    inspiration,
    appreciation,
    activity,
    artAnalysis: passedArtAnalysis,
    techniques: passedTechniques,
    concepts: passedConcepts
  } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [error, setError] = useState(null);
  const [aiPoints, setAiPoints] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [artAnalysis, setArtAnalysis] = useState(null);
  const [concepts, setConcepts] = useState([]);
  const [showTechniques, setShowTechniques] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [mentorResponse, setMentorResponse] = useState('');
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showTipBox, setShowTipBox] = useState(false);
  const [techniquesLoading, setTechniquesLoading] = useState(false);
  const [visibleBoxIndex, setVisibleBoxIndex] = useState(null);
  const [showTechniquePopup, setShowTechniquePopup] = useState(false);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [showThink, setShowThink] = useState(false);
  const [showImprove, setShowImprove] = useState(false);
  const [showReimagineModal, setShowReimagineModal] = useState(false);
  
  // Accordion state for right panel sections
  const [activeSection, setActiveSection] = useState(null);
  
  // Toggle accordion section
  const toggleSection = (sectionName) => {
    setActiveSection(activeSection === sectionName ? null : sectionName);
  };
  
  // Force browser/back navigation to return to Magic landing
  useEffect(() => {
    try {
      const onPop = (e) => {
        e.preventDefault();
        try { navigate('/magic', { replace: true }); } catch (_) {}
        window.location.replace('/magic');
      };
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
    } catch (_) {}
  }, [navigate]);

  // Add ref to track if component is mounted
  const isMountedRef = useRef(true);
  
  // Add ref to track if fetch is in progress to prevent duplicate calls
  const fetchInProgressRef = useRef(false);
  
  // Store timeouts and abort controller for cleanup
  const activeTimeoutsRef = useRef([]);
  const abortControllerRef = useRef(null);
  
  // Add state to track when data is ready for display
  const [dataReady, setDataReady] = useState(false);
  const [forceRender, setForceRender] = useState(0);
  
  // Add coordinate validation function
  const validateCoordinates = (point, index) => {
    if (!point || !point.point || !point.text || !point.label) {
      return { isValid: false, issues: ['Missing required data'] };
    }
    
    const issues = [];
    const { x, y } = point.point;
    const text = point.text.toLowerCase();
    const label = point.label.toLowerCase();
    
    // Check if coordinates are within bounds
    if (x < 0 || x > 1 || y < 0 || y > 1) {
      issues.push('Coordinates out of bounds');
    }
    
    // Check for common coordinate-description mismatches
    if (text.includes('corner') || label.includes('corner')) {
      if (x > 0.3 && x < 0.7 && y > 0.3 && y < 0.7) {
        issues.push('Describes corner but coordinates are in center');
      }
    }
    
    if (text.includes('top') || label.includes('top')) {
      if (y > 0.5) {
        issues.push('Describes top but coordinates are in bottom half');
      }
    }
    
    if (text.includes('bottom') || label.includes('bottom')) {
      if (y < 0.5) {
        issues.push('Describes bottom but coordinates are in top half');
      }
    }
    
    if (text.includes('left') || label.includes('left')) {
      if (x > 0.5) {
        issues.push('Describes left but coordinates are in right half');
      }
    }
    
    if (text.includes('right') || label.includes('right')) {
      if (x < 0.5) {
        issues.push('Describes right but coordinates are in left half');
      }
    }
    
    if (text.includes('center') || label.includes('center')) {
      if (x < 0.3 || x > 0.7 || y < 0.3 || y > 0.7) {
        issues.push('Describes center but coordinates are not in center');
      }
    }
    
    return { isValid: issues.length === 0, issues };
  };
  
  // Helper function to get processing message
  const getProcessingMessage = () => {
    return loadingProgress || 'Artmosphere is analysing your beautiful artwork, give a moment';
  };



  useEffect(() => {
    // Preload background image
    const img = new Image();
    img.onload = () => setBackgroundLoaded(true);
    // img.src = artistpublicprofileBackground; // Using CSS gradient fallback instead
  }, []);

  useEffect(() => {
    if (!imageUrl && typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('artmosphere_last_appreciation');
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached && cached.imageUrl) {
            setAiPoints(sortAppreciationPointsByVisualTopToBottom(cached.appreciation || []));
            setConcepts(cached.concepts || []);
            setTechniques(cached.techniques || []);
            setArtAnalysis(cached.artAnalysis || null);
          }
        }
      } catch (_) {}
    }
  }, [imageUrl]);

  useEffect(() => {
    if (imageUrl) {
      console.log('🔄 useEffect triggered - checking for existing data');
      
      // Check if we already have appreciation data from mentor flow
      if (appreciation && appreciation.length > 0) {
        console.log('✅ Using passed appreciation data from mentor flow');
        
        // Process the passed appreciation data to ensure correct structure
        let processedAppreciation = [];
        if (Array.isArray(appreciation)) {
          // Check if it's already in the correct format
          if (appreciation[0] && appreciation[0].point && appreciation[0].text) {
            // Already in correct format
            processedAppreciation = appreciation;
          } else {
            // Convert from simple text array to points format
            processedAppreciation = appreciation.map((text, index) => ({
              point: {
                x: 0.15, // Left side of artwork
                y: 0.2 + (index * 0.2)  // Distribute points vertically in a column
              },
              label: `Point ${index + 1}`,
              text: text,
              think: [`What do you notice about this area?`, `How does this element contribute to the overall composition?`],
              improve: [`Try adding more detail to this area`, `Experiment with different techniques here`]
            }));
          }
        }
        
        processedAppreciation = sortAppreciationPointsByVisualTopToBottom(processedAppreciation);
        setAiPoints(processedAppreciation);
        setConcepts(passedConcepts || []);
        setTechniques(passedTechniques || []);
        setArtAnalysis(passedArtAnalysis || null);
        setLoading(false);
        setForceRender(prev => prev + 1);

        // Auto-save to mentor activity dashboard when data comes from mentor flow
        (async () => {
          try {
            const mentorClass = (typeof window !== 'undefined') ? (localStorage.getItem('mentorClass') || null) : null;
            const mentorSection = (typeof window !== 'undefined') ? (localStorage.getItem('mentorSection') || null) : null;
            const activityId = await saveAppreciationActivity({
              originalImageUrl: imageUrl,
              aiPoints: processedAppreciation,
              techniques: passedTechniques || [],
              artAnalysis: passedArtAnalysis || null,
              concepts: passedConcepts || [],
              title: title || 'Art Appreciation',
              description: `Artmosphere analysis of artwork with ${processedAppreciation?.length || 0} insights`,
              metadata: {
                context: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'mentor' : 'student',
                mentorId: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? user.uid : null,
                orgId: user?.orgId || null,
                className: mentorClass,
                section: mentorSection,
                feature: 'art-appreciation'
              }
            });
            console.log('✅ Appreciation activity (passed data) saved with ID:', activityId);
          } catch (activityErr) {
            console.warn('⚠️ Failed to auto-save appreciation (passed data):', activityErr);
          }
        })();
        return;
      }
      
      // If no passed data, check if user explicitly started analysis
      // Only auto-fetch if startAnalysis flag is set (user clicked "Start Analysis" button)
      const shouldStartAnalysis = location.state?.startAnalysis === true;
      
      if (shouldStartAnalysis) {
        // Prevent duplicate calls
        if (fetchInProgressRef.current) {
          console.log('⚠️ Fetch already in progress, skipping duplicate call');
          return;
        }
        console.log('🔄 User explicitly started analysis - starting appreciation fetch');
        setLoading(true);
        setLoadingProgress('Initializing artwork analysis...');
        setForceRender(prev => prev + 1);
        fetchAppreciation(imageUrl, title, technique, inspiration);
      } else {
        console.log('ℹ️ Landing page view - waiting for user to start analysis');
        // Don't auto-fetch - show landing page or wait for user action
        setLoading(false);
      }
    }
    
    // Cleanup function to mark component as unmounted and clear timeouts
    return () => {
      console.log('🔄 Component cleanup - marking as unmounted and clearing timeouts');
      isMountedRef.current = false;
      fetchInProgressRef.current = false;
      
      // Clear all active timeouts
      activeTimeoutsRef.current.forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      activeTimeoutsRef.current = [];
      
      // Abort any ongoing fetch requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [imageUrl, appreciation, activity, passedArtAnalysis, passedTechniques, passedConcepts, location.state?.startAnalysis]);

  // Reset think/improve toggles when switching selected point
  useEffect(() => {
    setShowThink(false);
    setShowImprove(false);
  }, [visibleBoxIndex]);

  // Add a separate useEffect to monitor loading state changes
  useEffect(() => {
    console.log('🔄 Loading state changed to:', loading, 'at', new Date().toISOString());
  }, [loading]);

  // Monitor when data is ready and end loading
  useEffect(() => {
    if (dataReady && loading) {
      console.log('✅ Data is ready, ending loading state');
      console.log('📊 Current data state:', {
        aiPoints: aiPoints.length,
        techniques: techniques.length,
        artAnalysis: !!artAnalysis
      });
      
      // Ensure we have at least some data before ending loading
      const hasData = aiPoints.length > 0 || techniques.length > 0 || artAnalysis;
      
      console.log('🔍 Data validation check:', {
        aiPointsLength: aiPoints.length,
        techniquesLength: techniques.length,
        hasArtAnalysis: !!artAnalysis,
        hasData: hasData
      });
      
      if (!hasData) {
        console.log('⚠️ No data available yet, keeping loading overlay active');
        // Wait a bit more and try again
        setTimeout(() => {
          const retryHasData = aiPoints.length > 0 || techniques.length > 0 || artAnalysis;
          if (retryHasData) {
            console.log('✅ Data now available, proceeding to end loading');
            setTimeout(() => {
              console.log('🎯 Ending loading overlay after ensuring data is displayed');
              setLoading(false);
              setLoadingProgress('');
              setForceRender(prev => prev + 1);
            }, 2000);
          } else {
            console.log('⚠️ Still no data after retry, forcing loading end');
            setLoading(false);
            setLoadingProgress('');
            setForceRender(prev => prev + 1);
          }
        }, 2000);
        return;
      }
      
      // Add a longer delay to ensure all UI elements are fully rendered and visible
      setTimeout(() => {
        console.log('🎯 Ending loading overlay after ensuring data is displayed');
        setLoading(false);
        setLoadingProgress('');
        setForceRender(prev => prev + 1);
      }, 3000); // Wait 3 seconds to ensure everything is displayed
    }
  }, [dataReady, loading, aiPoints.length, techniques.length, artAnalysis]);

  const fetchAppreciation = async (imageUrl, title, technique, inspiration) => {
    // Prevent duplicate calls
    if (fetchInProgressRef.current) {
      console.log('⚠️ fetchAppreciation already in progress, skipping duplicate call');
      return;
    }
    
    fetchInProgressRef.current = true;
    console.log('🔄 fetchAppreciation called - setting loading to true');
    setLoading(true);
    setLoadingProgress('Artmosphere is analysing your beautiful artwork, give a moment');
    setError(null);
    setForceRender(prev => prev + 1);
    
    const processStartTime = Date.now();
    const minProcessTime = 5000; // Minimum 5 seconds for the entire process
    
    // Add a safety timeout to ensure loading doesn't get stuck
    const safetyTimeout = setTimeout(() => {
      console.log('⚠️ Safety timeout reached, forcing loading to end');
      if (isMountedRef.current) {
        setLoading(false);
        setLoadingProgress('');
        setForceRender(prev => prev + 1);
      }
      fetchInProgressRef.current = false;
    }, 130000); // 130 seconds maximum (to match backend timeout of 75s + image conversion + buffer)
    
    // Store timeout for cleanup
    activeTimeoutsRef.current.push(safetyTimeout);
    
    try {
      // Convert blob URL to base64 data URL FIRST, before cache check, so we can properly match activities
      let processedImageUrl = imageUrl;
      let cacheCheckUrl = imageUrl; // Use for cache checking
      try {
        if (typeof processedImageUrl === 'string' && processedImageUrl.startsWith('blob:')) {
          console.log('🖼️ Converting blob URL to base64 for backend and cache check');
          const resp = await fetch(processedImageUrl);
          const blob = await resp.blob();
          processedImageUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          // Use the base64 URL for cache checking (stable hash)
          cacheCheckUrl = processedImageUrl;
          console.log('✅ Converted to base64, length:', processedImageUrl.length);
        }
      } catch (blobErr) {
        console.warn('Blob to base64 conversion failed, continuing with original URL:', blobErr?.message || blobErr);
      }
      
      // Check cache in Activity Dashboard first to save cost (use converted URL for stable matching)
      try {
        console.log('🔍 Checking for existing appreciation to avoid duplicate generation...');
        const existing = await checkExistingActivity(cacheCheckUrl, ACTIVITY_TYPES.APPRECIATION);
        if (existing && existing.aiPoints && existing.aiPoints.length > 0) {
          console.log('✅ Using cached appreciation from Activity Dashboard');
          setAiPoints(sortAppreciationPointsByVisualTopToBottom(existing.aiPoints || []));
          setTechniques(existing.techniques || []);
          setArtAnalysis(existing.artAnalysis || null);
          clearTimeout(safetyTimeout);
          // Remove from active timeouts
          activeTimeoutsRef.current = activeTimeoutsRef.current.filter(t => t !== safetyTimeout);
          setDataReady(true);
          fetchInProgressRef.current = false;
          return; // Skip API call
        }
        console.log('ℹ️ No cached appreciation found; proceeding to generate');
      } catch (cacheErr) {
        console.warn('⚠️ Cache check failed, proceeding to generate:', cacheErr?.message || cacheErr);
      }
      
      const quotaCheck = checkQuotaBeforeUse('individualArtAppreciation');
      if (!quotaCheck.allowed) {
        console.log('⚠️ Individual art appreciation quota exceeded');
        clearTimeout(safetyTimeout);
        activeTimeoutsRef.current = activeTimeoutsRef.current.filter(t => t !== safetyTimeout);
        fetchInProgressRef.current = false;
        setLoading(false);
        return;
      }
      
      console.log('🔄 Making API call to backend...');
      setLoadingProgress('Artmosphere is analysing your beautiful artwork, give a moment');
      
      // Use background worker with polling
      let data;
      try {
        data = await enqueueAndPoll(
          API_ENDPOINTS.generateAppreciationFirebase || API_ENDPOINTS.generateAppreciation,
          {
            imageUrl: processedImageUrl,
            title,
            technique: technique || 'Unknown Artist',
            inspiration: inspiration || 'Contemporary'
          },
          {
            interval: 2000, // Poll every 2 seconds
            maxAttempts: 150, // 5 minutes max
            onProgress: (status, job, attempts) => {
              if (isMountedRef.current) {
                setLoadingProgress(`Artmosphere is analysing your beautiful artwork... (${attempts * 2}s)`);
              }
            },
            onStatusChange: (status) => {
              console.log('🔄 Job status changed:', status);
            },
            baseUrl: 'https://us-central1-artmosphere-89b64.cloudfunctions.net/api'
          }
        );
      } catch (pollError) {
        console.error('❌ Error in enqueueAndPoll:', pollError);
        throw pollError; // Re-throw to be caught by outer catch
      }
      const endTime = Date.now();
      console.log(`✅ API call successful in ${endTime - processStartTime}ms, setting data...`);
      if (data.appreciationPromptVersion) {
        console.log('✅ appreciationPromptVersion (new Art History–aligned worker):', data.appreciationPromptVersion);
      }
      console.log('📊 Data received:', { 
        appreciationPromptVersion: data.appreciationPromptVersion || '(not set — deploy functions with latest worker)',
        pointsCount: data.points?.length || 0,
        hasTechniques: !!data.techniques?.length,
        hasArtAnalysis: !!data.artAnalysis,
        conceptsCount: data.concepts?.length || 0
      });
      
      // Log token usage if available
      if (data.usage) {
        console.log('💰 Token Usage:');
        console.log(`  📝 Prompt tokens: ${data.usage.prompt_tokens || 0}`);
        console.log(`  ✍️  Completion tokens: ${data.usage.completion_tokens || 0}`);
        console.log(`  📊 Total tokens: ${data.usage.total_tokens || 0}`);
        console.log(`  Model: ${data.usage.model || 'N/A'}`);
        console.log(`  💵 Estimated cost: $${((data.usage.total_tokens || 0) * 0.01 / 1000).toFixed(4)} (at $0.01 per 1K tokens for gpt-4o)`);
      }
      
      // Check if component is still mounted before processing data
      if (!isMountedRef.current) {
        console.log('⚠️ Component unmounted during API call, stopping processing');
        return;
      }
      

      
      // Process all data before setting loading to false
      console.log('🔄 Processing data for frontend display...');
      setLoadingProgress('Artmosphere is analysing your beautiful artwork, give a moment');
      
      // Convert backend response to frontend format
      let processedPoints = [];
      if (data.points && Array.isArray(data.points)) {
        // New format with points array
        processedPoints = data.points.map((p, idx) => ({
          ...p,
          point: {
            x: typeof p.point?.x === 'number' ? p.point.x : 0.15,
            y: typeof p.point?.y === 'number' ? p.point.y : (0.2 + (idx * 0.2)),
          },
          // Use improve suggestions from backend if provided, otherwise use empty array (don't use generic fallback)
          improve: Array.isArray(p?.improve) && p.improve.length > 0 ? p.improve : []
        }));
      } else if (data.appreciation && Array.isArray(data.appreciation)) {
        // Convert appreciation array (strings or objects) to points format
        const items = data.appreciation;
        processedPoints = items
          .map((item, index) => {
            const textVal = typeof item === 'string'
              ? item
              : (item?.text || item?.appreciation || item?.title || '');
            // Skip entries without text (e.g., Mentor's Suggestion objects)
            if (!textVal || typeof textVal !== 'string') return null;
            return {
              point: {
                x: 0.15,
                y: 0.2 + (index * 0.2)
              },
              label: `Point ${index + 1}`,
              text: textVal,
              improve: Array.isArray(item?.improve) && item.improve.length ? item.improve : [`Try adding more detail to this area`, `Experiment with different techniques here`]
            };
          })
          .filter(Boolean);
      }

      processedPoints = sortAppreciationPointsByVisualTopToBottom(processedPoints);
      
      console.log('🔄 Setting aiPoints:', processedPoints.length, 'points');
      setAiPoints(processedPoints);
      
      

      // Concepts from backend for Did You Know section
      setConcepts(Array.isArray(data.concepts) ? data.concepts : []);
      
      // Fix: Extract techniques from artAnalysis structure and deduplicate
      const techniquesFromArtAnalysis = data.artAnalysis?.techniques || [];
      const techniquesFromRoot = data.techniques || [];
      
      console.log('🔄 Processing techniques...', {
        artAnalysisCount: techniquesFromArtAnalysis.length,
        rootCount: techniquesFromRoot.length
      });
      
      // Create a Map to deduplicate techniques by name
      const techniqueMap = new Map();
      
      // Helper function to normalize technique names for comparison
      const normalizeName = (name) => {
        return name.toLowerCase().trim().replace(/[^\w\s]/g, '');
      };
      
      // Add techniques from artAnalysis first (they take priority)
      techniquesFromArtAnalysis.forEach(technique => {
        if (technique.name) {
          const normalizedName = normalizeName(technique.name);
          techniqueMap.set(normalizedName, technique);
        }
      });
      
      // Add techniques from root only if they don't already exist
      techniquesFromRoot.forEach(technique => {
        if (technique.name) {
          const normalizedName = normalizeName(technique.name);
          if (!techniqueMap.has(normalizedName)) {
            techniqueMap.set(normalizedName, technique);
          } else {
            console.log(`Duplicate technique found: "${technique.name}" - skipping`);
          }
        }
      });
      
      // Convert Map back to array
      const allTechniques = Array.from(techniqueMap.values());
      
      console.log('✅ All data processing complete, setting final state...');
      console.log('📊 Final data:', {
        techniquesCount: allTechniques.length,
        pointsCount: data.points?.length || 0,
        conceptsCount: (Array.isArray(data.concepts) ? data.concepts.length : 0)
      });
      
      // Check if component is still mounted before setting final state
      if (!isMountedRef.current) {
        console.log('⚠️ Component unmounted during data processing, stopping');
        return;
      }
      
      // Set all state at once to ensure data is ready
      console.log('🔄 Setting techniques:', allTechniques.length, 'techniques');
      setTechniques(allTechniques);
      
      console.log('🔄 Setting artAnalysis:', !!data.artAnalysis);
      setArtAnalysis(data.artAnalysis || null);
      
      // Wait for React to process state updates and re-render
      console.log('⏳ Waiting for React state updates and re-render...');
      setLoadingProgress('Artmosphere is analysing your beautiful artwork, give a moment');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Double-check that component is still mounted
      if (!isMountedRef.current) {
        console.log('⚠️ Component unmounted during render wait, stopping');
        return;
      }
      
      console.log('✅ All data is now ready for frontend display');
      
      // Ensure minimum loading time and data validation
      const totalProcessTime = Date.now() - processStartTime;
      const remainingTime = Math.max(0, minProcessTime - totalProcessTime);
      if (remainingTime > 0) {
        console.log(`🎨 Process completed quickly (${totalProcessTime}ms). Adding ${remainingTime}ms to ensure minimum loading time...`);
        setLoadingProgress('Artmosphere is analysing your beautiful artwork, give a moment');
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Additional validation to ensure data is properly set
      console.log('🔍 Final data validation before ending loading:', {
        aiPointsCount: data.points?.length || 0,
        techniquesCount: data.artAnalysis?.techniques?.length || 0,
        movementsCount: data.artAnalysis?.movements?.length || 0
      });
      
      // Mark data as ready and ensure proper display
      console.log('🎯 Setting dataReady to true - this will trigger loading end');
      clearTimeout(safetyTimeout); // Clear the safety timeout
      activeTimeoutsRef.current = activeTimeoutsRef.current.filter(t => t !== safetyTimeout);
      setDataReady(true);
      fetchInProgressRef.current = false; // Mark fetch as complete
      
      await recordUsage('individualArtAppreciation');
      
      // Save activity to dashboard (use processed/base64 URL for stable matching)
      try {
        console.log('🔄 Attempting to save appreciation activity...');
        const mentorClass = (typeof window !== 'undefined') ? (localStorage.getItem('mentorClass') || null) : null;
        const mentorSection = (typeof window !== 'undefined') ? (localStorage.getItem('mentorSection') || null) : null;
        const activityId = await saveAppreciationActivity({
          originalImageUrl: cacheCheckUrl, // Use processed/base64 URL for stable hash matching
          aiPoints: processedPoints,
          techniques: allTechniques,
          artAnalysis: data.artAnalysis || null,
          concepts: Array.isArray(data.concepts) ? data.concepts : [],
          title: title || 'Art Appreciation',
          description: `Artmosphere analysis of artwork with ${data.points?.length || 0} insights`,
          metadata: {
            context: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'mentor' : 'student',
            mentorId: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? user.uid : null,
            orgId: user?.orgId || null,
            className: mentorClass,
            section: mentorSection
          }
        });
        console.log('✅ Appreciation activity saved to dashboard with ID:', activityId);
      } catch (activityError) {
        console.warn('⚠️ Failed to save appreciation activity:', activityError);
      }
      
      // Add additional delay to ensure all UI elements are fully rendered
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Double-check component is still mounted before ending loading
      if (!isMountedRef.current) {
        console.log('⚠️ Component unmounted during final wait, stopping');
        return;
      }
      
    } catch (error) {
      console.log('❌ Error in fetchAppreciation:', error.message);
      clearTimeout(safetyTimeout); // Clear the safety timeout
      activeTimeoutsRef.current = activeTimeoutsRef.current.filter(t => t !== safetyTimeout);
      fetchInProgressRef.current = false; // Mark fetch as complete even on error
      if (isMountedRef.current) {
        setError(error.message);
        setLoadingProgress('Error occurred during analysis...');
        
        // Wait a bit even on error to show the error state
        const totalProcessTime = Date.now() - processStartTime;
        const remainingTime = Math.max(0, minProcessTime - totalProcessTime);
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        setLoading(false);
        setLoadingProgress('');
        setForceRender(prev => prev + 1);
      }
    }
  };

  const handleMentorQuery = async () => {
    if (!userQuery.trim()) return;
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.askMentor, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: userQuery }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get mentor response");
      }
      const data = await response.json();
      setMentorResponse(data.message);
      setUserQuery("");
    } catch (error) {
      setError(error.message || "Failed to get mentor response");
    } finally {
      setLoading(false);
    }
  };

  const handleTechniquesClick = () => {
    setShowTechniques(!showTechniques);
  };

  return (
    <div className="artm-page artm-page-pad min-h-screen h-screen flex flex-col relative bg-[#FFF8E1] px-0 py-0 md:px-[2.5cm] md:py-6">
      <UpgradeQuotaModal
        open={showUpgradeModal}
        onClose={dismissUpgradeModal}
        feature={upgradeModalFeature}
      />
      {/* Background - behind window */}
      <div className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500 ${backgroundLoaded ? 'opacity-100' : 'opacity-0'}`} style={{ backgroundImage: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
      {!backgroundLoaded && <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200" />}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-stone-900/40 backdrop-blur-xl">
          <div className="bg-white rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 border-4 border-amber-300">
            <svg className="animate-spin h-12 w-12 text-amber-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <div className="text-amber-700 font-bold text-lg text-center mb-2">Artmosphere is analyzing your beautiful artwork</div>
            <div className="text-amber-600 text-sm text-center">Please wait while we analyze your artwork</div>
          </div>
        </div>
      )}

      <div className="relative z-10 w-full max-w-full flex-1 min-h-0 flex flex-col py-0 md:py-4 flex-grow">
        <div className="bg-white rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 flex flex-col flex-1 min-h-0 overflow-hidden min-h-[50vh]">
          <header className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
            <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px] px-3 md:px-6">
              <p className="text-sm md:text-base font-semibold text-gray-800 truncate pr-2">Art Appreciation &amp; Tech Insights</p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button type="button" onClick={() => navigate('/my-art-world')} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all">Artist corner</button>
                <button type="button" onClick={() => navigate((user && (user.accountType === 'mentor' || user.role === 'mentor')) ? '/mentor-activity-dashboard' : '/activity-dashboard?view=magic-studio', { state: { view: (user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'mentor' : 'magic-studio' } })} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all">{(user && (user.accountType === 'mentor' || user.role === 'mentor')) ? 'Mentor Activity Dashboard' : 'My Activities'}</button>
              </div>
            </div>
          </header>
          <div className="flex-1 min-h-0 overflow-auto max-w-7xl mx-auto w-full p-3 md:p-6 lg:p-8 flex flex-col lg:flex-row gap-4 md:gap-6">
        {/* Left Side - Artwork */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center order-1 lg:order-1">
          <div className="relative w-full">
            {imageUrl && (
              <div className="relative bg-white rounded-xl md:rounded-2xl shadow-2xl p-2 md:p-4">
            <img
              src={imageUrl}
                  alt={title || "Artwork"}
                  className="w-full h-auto max-h-[56vh] sm:max-h-[58vh] md:max-h-[calc(100dvh-14rem)] lg:max-h-[calc(100dvh-15rem)] object-contain rounded-lg md:rounded-xl"
                />
                
                {/* Analysis Points Overlay */}
                {aiPoints.length > 0 && (
                  <div className="absolute inset-2 md:inset-4">
                    {aiPoints.map((point, index) => {
                      const nx = point.point?.x;
                      const ny = point.point?.y;
                      const useApi =
                        typeof nx === 'number' &&
                        typeof ny === 'number' &&
                        nx >= 0 &&
                        nx <= 1 &&
                        ny >= 0 &&
                        ny <= 1;
                      const xPctRaw = useApi ? nx * 100 : 12;
                      const yPctRaw = useApi
                        ? ny * 100
                        : ((index + 1) / (aiPoints.length + 1)) * 100;
                      // Keep circles inside the overlay after translate(-50%,-50%)
                      const xPct = useApi ? Math.min(92, Math.max(8, xPctRaw)) : xPctRaw;
                      const yPct = useApi ? Math.min(90, Math.max(10, yPctRaw)) : Math.min(88, Math.max(12, yPctRaw));
                      return (
                        <button
                          key={index}
                          onClick={() => setVisibleBoxIndex(visibleBoxIndex === index ? null : index)}
                          className={`absolute w-10 h-10 md:w-12 md:h-12 text-white rounded-full flex items-center justify-center text-sm md:text-base font-bold shadow-lg transition-all duration-200 z-10 ${
                            visibleBoxIndex === index 
                              ? 'bg-amber-600 scale-110 ring-2 md:ring-4 ring-amber-200' 
                              : 'bg-amber-500 hover:bg-amber-600 hover:scale-105'
                          }`}
                          style={{
                            left: `${xPct}%`,
                            top: `${yPct}%`,
                            transform: 'translate(-50%, -50%)'
                          }}
                        >
                          {index + 1}
                        </button>
              );
            })}
                  </div>
                )}
              </div>
            )}
            
            {/* Analysis Status - suppress failure banner; UI will still show right panel cards */}
            {false && aiPoints.length === 0 && !loading && (
              <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-lg">
                <p className="text-red-700 text-center">No analysis points found. Please try again.</p>
              </div>
            )}
            
            {/* Reimagine Button moved to right panel */}
                    </div>
        </div>
 
        {/* Right Side - Analysis Panel - Below artwork on mobile */}
        <div className="w-full lg:w-1/2 order-2 lg:order-2">
          <div className="bg-white/70 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 lg:p-8 lg:sticky lg:top-24 max-h-[calc(100vh-12rem)] md:max-h-[calc(100vh-8rem)] overflow-y-auto">
            {/* Dynamic Content Based on Selected Point */}
            {visibleBoxIndex !== null && aiPoints[visibleBoxIndex] ? (
              <div className="space-y-4 md:space-y-6">
                <div className="border-b border-gray-200 pb-3 md:pb-4">
                  <div className="flex items-center justify-between">
                    <div className="text-center flex-1 min-w-0 pr-2">
                  <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">
                    {aiPoints[visibleBoxIndex].label}
                  </h2>
                  <div className="inline-flex items-center px-2 md:px-3 py-0.5 md:py-1 bg-amber-100 text-amber-800 rounded-full text-xs md:text-sm font-medium">
                    Point {visibleBoxIndex + 1} of {aiPoints.length}
                  </div>
                </div>
                        <button
                    onClick={() => setVisibleBoxIndex(null)} 
                      className="ml-2 md:ml-3 px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300 flex-shrink-0"
                        >
                      Close
                        </button>
                </div>
              </div>
                
                {/* Combined Appreciation + Suggestions */}
                <div className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 border border-amber-200">
                    <h3 className="font-semibold text-gray-900 mb-3 md:mb-4 flex items-center">
                    <span className="w-6 h-6 md:w-8 md:h-8 rounded-full border border-amber-300 text-amber-700 flex items-center justify-center text-xs md:text-sm font-bold mr-2 md:mr-3 flex-shrink-0">
                      {visibleBoxIndex + 1}
                    </span>
                    <span className="tracking-tight text-base md:text-xl">Did YOU KNOW?</span>
                    </h3>
                  <p className="text-gray-700 leading-relaxed text-sm md:text-lg mb-3 md:mb-4">
                    {aiPoints[visibleBoxIndex].text}
                  </p>
                {aiPoints[visibleBoxIndex].improve && aiPoints[visibleBoxIndex].improve.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3 md:p-5 border border-gray-200">
                      <p className="text-xs md:text-sm font-medium text-gray-800 mb-2 md:mb-3">Artmosphere tips</p>
                      <ul className="space-y-1.5 md:space-y-2">
                      {aiPoints[visibleBoxIndex].improve.map((suggestion, sIndex) => (
                          <li key={sIndex} className="text-gray-800 flex items-start">
                            <span className="w-6 h-6 rounded-full border border-gray-300 text-gray-700 flex items-center justify-center text-xs font-semibold mr-3 mt-0.5 flex-shrink-0">
                            {sIndex + 1}
                          </span>
                            <span className="text-base leading-relaxed">{suggestion}</span>
                        </li>
                          ))}
                        </ul>
                      <div className="mt-4 flex justify-end">
                <button
                          onClick={() => setShowReimagineModal(true)}
                          className="px-5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-200 font-semibold shadow"
                >
                          Reimagine your artwork
                </button>
                  </div>
              </div>
            )}
            </div>
                  </div>
            ) : (
              /* Default Content When No Point Selected */
              <div className="space-y-6">
                <div className="text-center border-b border-gray-200 pb-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-3">Art Appreciation & Tech Insights</h2>
                  <p className="text-gray-600 text-lg">
                    Click on the numbered buttons on the artwork to explore detailed insights
                  </p>
              </div>
                
                {/* Technique Insights - FIRST BUTTON */}
                {aiPoints && aiPoints.length > 0 && aiPoints[0].improve && (
                  <div className="bg-white rounded-2xl shadow-lg border border-amber-200 overflow-hidden">
                <button
                      onClick={() => toggleSection('techniqueInsights')}
                      className={`w-full p-6 text-left transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-inset hover:scale-[1.02] ${activeSection === 'techniqueInsights' ? 'bg-amber-50 border-b border-amber-200' : 'hover:bg-amber-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900">Did YOU KNOW?</h3>
                        <div className={`transform transition-transform duration-300 ${activeSection === 'techniqueInsights' ? 'rotate-180' : 'rotate-0'}`}>
                          <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                </button>
                  <motion.div
                      initial={false}
                      animate={{
                        height: activeSection === 'techniqueInsights' ? 'auto' : 0,
                        opacity: activeSection === 'techniqueInsights' ? 1 : 0
                      }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 space-y-3">
                        {aiPoints.map((point, index) => (
                      <motion.div
                        key={index}
                            className="flex items-start space-x-3"
                        initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                              opacity: activeSection === 'techniqueInsights' ? 1 : 0,
                              y: activeSection === 'techniqueInsights' ? 0 : 20
                            }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                          >
                            <div className="w-7 h-7 rounded-full border border-amber-300 text-amber-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-semibold">{index + 1}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-700 leading-relaxed mb-2">{point.text}</p>
                              {point.improve && point.improve.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-sm font-medium text-gray-700 mb-2">Artmosphere tips</p>
                                  <ul className="space-y-1">
                                    {point.improve.map((improvement, impIndex) => (
                                      <li key={impIndex} className="text-sm text-gray-700 flex items-start">
                                        <span className="text-green-600 mr-2">•</span>
                                        <span>{improvement}</span>
                                      </li>
                                    ))}
                                  </ul>
                  </div>
                )}
              </div>
                      </motion.div>
                    ))}
                      </div>
                  </motion.div>
              </div>
            )}
                
                {/* Art Analysis - Matching Art History Design */}
                {artAnalysis && (
                  <div className="space-y-6">
                    {/* Techniques */}
                    {artAnalysis.techniques && artAnalysis.techniques.length > 0 && (
                      <div className="bg-white rounded-2xl shadow-lg border border-amber-200 overflow-hidden">
              <button
                          onClick={() => toggleSection('techniques')}
                          className="w-full p-6 text-left hover:bg-amber-50 hover:scale-[1.02] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-inset"
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">Did you know this technique looks like?</h3>
                            <div className={`transform transition-transform duration-300 ${activeSection === 'techniques' ? 'rotate-180' : 'rotate-0'}`}>
                              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
              </div>
                          </div>
              </button>
                  <motion.div
                          initial={false}
                          animate={{
                            height: activeSection === 'techniques' ? 'auto' : 0,
                            opacity: activeSection === 'techniques' ? 1 : 0
                          }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6 space-y-4">
                          {artAnalysis.techniques.map((technique, index) => (
                      <motion.div
                        key={index}
                                className="bg-gray-50 rounded-lg p-4"
                        initial={{ opacity: 0, y: 20 }}
                                animate={{ 
                                  opacity: activeSection === 'techniques' ? 1 : 0,
                                  y: activeSection === 'techniques' ? 0 : 20
                                }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                              >
                                <h5 className="font-semibold text-gray-900 text-lg mb-2">{technique.name}</h5>
                              <p className="text-gray-700 mb-2">{technique.description}</p>
                                {technique.location && (
                                  <p className="text-gray-500 text-xs mt-2">📍 {technique.location}</p>
                                )}
                                {technique.example && (
                                  <p className="text-gray-500 text-xs mt-1">💡 Example: {technique.example}</p>
                                )}
                              </motion.div>
                          ))}
                            </div>
                        </motion.div>
                              </div>
                )}
                    
                    {/* Art Movements */}
                    {artAnalysis.movements && artAnalysis.movements.length > 0 && (
                      <div className="bg-white rounded-2xl shadow-lg border border-blue-200 overflow-hidden">
                              <button
                          onClick={() => toggleSection('movements')}
                          className="w-full p-6 text-left hover:bg-blue-50 hover:scale-[1.02] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">Which Art movement this artwork could belong to?</h3>
                            <div className={`transform transition-transform duration-300 ${activeSection === 'movements' ? 'rotate-180' : 'rotate-0'}`}>
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              </div>
                </div>
                        </button>
          <motion.div
                          initial={false}
                          animate={{
                            height: activeSection === 'movements' ? 'auto' : 0,
                            opacity: activeSection === 'movements' ? 1 : 0
                          }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6 space-y-4">
                          {artAnalysis.movements.map((movement, index) => (
            <motion.div
                                key={index} 
                                className="bg-gray-50 rounded-lg p-4"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ 
                                  opacity: activeSection === 'movements' ? 1 : 0,
                                  y: activeSection === 'movements' ? 0 : 20
                                }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                              >
                                <h5 className="font-semibold text-gray-900 text-lg mb-2">{movement.name}</h5>
                              <p className="text-gray-700 mb-2">{movement.description}</p>
                              {movement.influence && (
                                  <p className="text-gray-600 text-sm italic bg-white rounded p-2">{movement.influence}</p>
                              )}
                                {movement.famousExample && (
                                  <p className="text-gray-500 text-xs mt-2">💡 Famous example: {movement.famousExample}</p>
                                )}
                      </motion.div>
                    ))}
              </div>
                  </motion.div>
                              </div>
                )}
              </div>
                )}

                {/* Concepts - Matching Art History Design */}
            {/* Key Concepts - always render with fallback if empty */}
            <div className="bg-white rounded-2xl shadow-lg border border-green-200 overflow-hidden">
          <button
                onClick={() => toggleSection('concepts')}
                className="w-full p-6 text-left hover:bg-green-50 hover:scale-[1.02] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-inset"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Key Concepts</h3>
                  <div className={`transform transition-transform duration-300 ${activeSection === 'concepts' ? 'rotate-180' : 'rotate-0'}`}>
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
          </button>
              <motion.div
                initial={false}
                animate={{
                  height: activeSection === 'concepts' ? 'auto' : 0,
                  opacity: activeSection === 'concepts' ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6 space-y-4">
                  {(concepts && concepts.length ? concepts : [
                    { title: 'Contrast', text: 'Contrast helps define focus and depth.' },
                    { title: 'Edges', text: 'Soft vs hard edges shape realism and mood.' },
                    { title: 'Color Harmony', text: 'Related hues create a cohesive feel.' }
                  ]).map((concept, index) => (
                    <motion.div 
                  key={index}
                      className="bg-gray-50 rounded-lg p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ 
                        opacity: activeSection === 'concepts' ? 1 : 0,
                        y: activeSection === 'concepts' ? 0 : 20
                      }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <h5 className="font-semibold text-gray-900 text-lg mb-2">{concept.title}</h5>
                      <p className="text-gray-700">{concept.text}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
                
                {/* Technique Insights section moved to top */}
                
              </div>
            )}
            
            {/* Ask Artmosphere Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                Ask Artmosphere
              </h3>
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={userQuery}
                  onChange={(e) => setUserQuery(e.target.value)}
                  placeholder="What do you want feedback on?"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-base"
                />
                <button
                  onClick={handleMentorQuery}
                  className="px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
                >
                  Ask
                </button>
                </div>
              {mentorResponse && (
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                  <p className="text-gray-700">{mentorResponse}</p>
                </div>
              )}
            </div>
        </div>
        </div>
          </div>
        </div>
        </div>
        
      {/* Reimagine Modal */}
      <AnimatePresence>
        {showReimagineModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowReimagineModal(false);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-7xl h-[min(96vh,920px)] max-h-[96vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border-2 border-amber-200/80"
            >
              <div className="flex flex-shrink-0 items-center justify-between gap-3 px-4 sm:px-6 py-3 md:py-4 border-b border-amber-200/70 bg-gradient-to-r from-amber-50 via-white to-amber-50/60 shadow-sm">
                <div className="min-w-0 flex-1 pr-2">
                  <p className="text-base md:text-lg font-semibold text-gray-900 truncate tracking-tight">
                    Re-imagine your artwork
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowReimagineModal(false)}
                  className="flex-shrink-0 p-2.5 bg-white text-amber-800 rounded-full border-2 border-amber-300 shadow-md hover:bg-amber-50 transition-all duration-200"
                  aria-label="Close modal"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto bg-[#FFF8E1]/40">
                <div className="relative p-3 sm:p-5 md:p-6 lg:p-8 min-h-0">
                  <ReimagineStudio
                    externalState={{
                      artwork: {
                        imageUrl: imageUrl,
                        originalArtwork: imageUrl,
                        title: title,
                        technique: technique,
                        inspiration: inspiration,
                        className: (typeof window !== 'undefined' && localStorage.getItem('mentorClass')) || null,
                        section: (typeof window !== 'undefined' && localStorage.getItem('mentorSection')) || null,
                        artist: (() => {
                          try {
                            if (typeof window !== 'undefined') {
                              const sel = localStorage.getItem('mentorSelectedArtwork');
                              if (sel) {
                                const parsed = JSON.parse(sel);
                                return parsed?.artist || null;
                              }
                            }
                          } catch (_) {}
                return null;
                        })()
                      }
                    }}
                    hideChrome={true}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
            )}
      </AnimatePresence>
    </div>
  );
} 
